import { MailboxProgress } from '@/api/client'
import clsx from 'clsx'

interface Props {
  progress?: MailboxProgress
  /** Mostra detalhes embaixo (bytes/items). Default: true */
  showDetails?: boolean
  /** Tamanho compacto pra usar em tabelas */
  compact?: boolean
}

export function MigrationProgressBar({ progress, showDetails = true, compact = false }: Props) {
  if (!progress) {
    return (
      <div className="text-xs text-slate-400 italic">Aguardando dados de progresso...</div>
    )
  }

  const { percentItems, transferredItems, estimatedItems, status } = progress
  const pct = Math.min(100, Math.max(0, percentItems))

  // Cores baseadas no status do wave_item (1=Pending, 2=Running, 3=Throttled, 4=Completed, 5=Failed, 6=Skipped, 7=Partial, 8=Paused)
  const barColor =
    status === 5 ? 'bg-red-500'        // Failed
    : status === 4 ? 'bg-green-500'    // Completed
    : status === 7 ? 'bg-amber-500'    // PartialSuccess
    : status === 8 ? 'bg-blue-400'     // Paused
    : status === 3 ? 'bg-orange-400'   // Throttled
    : 'bg-blue-500'                    // Running/Pending

  return (
    <div className={clsx('w-full', compact ? 'space-y-0.5' : 'space-y-1')}>
      <div className={clsx('w-full bg-slate-200 rounded-full overflow-hidden', compact ? 'h-1.5' : 'h-2')}>
        <div
          className={clsx('h-full transition-all duration-300', barColor)}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showDetails && (
        <div className="flex justify-between text-xs text-slate-500">
          <span>
            {transferredItems.toLocaleString()} / {estimatedItems > 0 ? estimatedItems.toLocaleString() : '?'} itens
          </span>
          <span className="font-medium">{pct.toFixed(1)}%</span>
        </div>
      )}
    </div>
  )
}

interface AggregateProps {
  totalTransferredItems: number
  totalEstimatedItems: number
  overallPercent: number
  elapsedSeconds: number
}

export function AggregateProgressBar({ totalTransferredItems, totalEstimatedItems, overallPercent, elapsedSeconds }: AggregateProps) {
  const pct = Math.min(100, Math.max(0, overallPercent))
  const itemsPerSec = elapsedSeconds > 0 ? totalTransferredItems / elapsedSeconds : 0
  const etaSeconds = itemsPerSec > 0 && totalEstimatedItems > totalTransferredItems
    ? (totalEstimatedItems - totalTransferredItems) / itemsPerSec
    : 0

  function formatDuration(s: number): string {
    if (s <= 0) return '—'
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    if (h > 0) return `${h}h ${m}m`
    if (m > 0) return `${m}min`
    return `${Math.round(s)}s`
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-baseline">
        <h3 className="text-sm font-semibold text-slate-700">Progresso total</h3>
        <span className="text-2xl font-bold text-slate-900">{pct.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-slate-200 rounded-full overflow-hidden h-3">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-slate-500">Itens migrados</div>
          <div className="font-medium text-slate-900">{totalTransferredItems.toLocaleString()}</div>
          <div className="text-slate-400">de {totalEstimatedItems.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-slate-500">Velocidade</div>
          <div className="font-medium text-slate-900">{Math.round(itemsPerSec).toLocaleString()} itens/s</div>
          <div className="text-slate-400">{Math.round(itemsPerSec * 3600).toLocaleString()}/h</div>
        </div>
        <div>
          <div className="text-slate-500">Tempo</div>
          <div className="font-medium text-slate-900">{formatDuration(elapsedSeconds)} decorrido</div>
          <div className="text-slate-400">~{formatDuration(etaSeconds)} restante</div>
        </div>
      </div>
    </div>
  )
}
